import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@ui/components/Button'
import { IconFCType } from '@ui/icons/types'
import { ChipByOption } from '@ui/v2/TableFilter/components/ChipByOption/ChipByOption'
import { useFilterHandlers } from '@ui/v2/TableFilter/hooks/useFilterHandlers'

import { useWindowResize } from '@utils/domUtils'

import { FilterQueryProps } from '@model/FilterFields'
import { FilterOptionsDto } from '@model/FilterOptionsDto'
import { NameDto } from '@model/NameDto'

import { DateShortcutOption } from './Chips/FilterChipModalDateRange'
import { FilterHorizontalScroll } from './FilterHorizontalScroll'

import styles from './TableFilter.module.scss'

export interface BadgeData {
  name: string
  value: string | number
  type: FilterOptions
}

export enum FilterOptions {
  ClientVolume,
  CAClientVolume,
  Amount,
  TradingAccountCount,
  SignedUpDate,
  VolumeFxm,
  VolumeOther,
  AccumulatedVolumeFxm,
  AccumulatedVolumeOther,
  TotalCommission,
  AccumulatedCommission,
  Commission,
  CACommission,
  RebateAmount,
  Date,
  Balance,
  TransactionDate,
  RegistrationDate,
  StatusChangedAtDate,
  CreatedDate,
  TradePeriod,
  VolumeSince2019,
  VolumeCurrentMonth,
  VolumePreviousMonth,
  ClientAmount,
  Volume,
  ClientIdAmount,
  Price,
  FxmVolume,
  OtherVolume,
  FxmCommission,
  OtherCommission,
  ClientName,
  LinkClicked,
  RegistrationStarted,
  EmailVerified,
  Activated,
  FirstTimeDeposit,
  FirstTimeTrade,
  LastTrade,
  Entity = 'ca_search_TickmillCompany',
  Status = 'ca_search_Status',
  RebateApprovalType = 'ca_search_RebateApprovalType',
  RebatePeriod = 'ca_search_RebatePeriod',
  Country = 'ca_search_Country',
  TradingAccountId = 'ca_search_TradingAccountId',
  ClientId = 'ca_search_ClientId',
  IntroducingBrokerGroupId = 'ca_search_IntroducingBrokerGroupId',
  AccountType = 'ca_search_AccountType',
  TradingAccountType = 'ca_search_TradingAccountType',
  RebateGroupId = 'ca_search_RebateGroupId',
  IBRebateGroupId = 'ca_search_GroupId',
  State = 'ca_search_State',
  Currency = 'ca_search_Currency',
  WalletCurrencyId = 'ca_search_WalletCurrencyId',
  ClientEntity = 'ca_search_ClientEntity',
  TransactionType = 'ca_search_TransactionTypeId',
  TransactionStatus = 'ca_search_TransactionStateId',
  CategoryId = 'ca_search_CategoryId',
  CampaignId = 'ca_search_CampaignId',
  LanguageId = 'ca_search_LanguageId',
  SizeId = 'ca_search_SizeId',
  Type = 'ca_search_Type',
  Wallet = 'ca_search_WalletId',
  SubscriptionType = 'ca_search_SubscriptionTypeId',
  SubscriptionLevel = 'ca_search_SubscriptionLevelId',
  SubscriptionClassificationType = 'ca_search_SubscriptionClassificationTypeId',
  SubscriptionStatus = 'ca_search_StateId',
  SubscriptionRequestType = 'ca_search_ClientAreaRequestTypeId',
  Name = 'ca_search_Name',
  TradingAccountPlatformType = 'ca_search_TradingAccountPlatformTypeId',
  TradingAccount = 'ca_search_TaId',
  HasTradingAccounts = 'query_search_HasTradingAccounts',
  Section = 'ca_search_Section',
  Format = 'ca_search_Format',
}

export interface SearchableEntityProps {
  options?: NameDto<string | number>[]
  setSearch?: (value?: FilterQueryProps) => void
}

export type TableFilterProps = {
  isVisible?: boolean

  approvalTypes?: NameDto[]
  rebatePeriods?: NameDto[]
  statusOptions?: FilterOptionsDto['statuses']
  tradingAccountTypeOptions?: FilterOptionsDto['accountTypes']
  sectionOptions?: FilterOptionsDto['sections']
  formatOptions?: FilterOptionsDto['formats']
  transactionTypes?: FilterOptionsDto['transactionTypes']
  transactionStates?: FilterOptionsDto['transactionStates']
  types?: NameDto[]
  wallets?: NameDto<string>[]
  tradingAccounts?: NameDto<string>[]
  platformTypes?: FilterOptionsDto['platformTypes']
  groupSearchProps?: SearchableEntityProps
  campaignSearchProps?: SearchableEntityProps
  categoriesSearchProps?: SearchableEntityProps
  languagesSearchProps?: SearchableEntityProps
  sizesSearchProps?: SearchableEntityProps
  tradingAccountSearchProps?: SearchableEntityProps
  clientsSearchProps?: SearchableEntityProps
  countryOptions?: FilterOptionsDto['countries']
  entityOptions?: FilterOptionsDto['entities']
  currencyOptions?: NameDto<string>[]
  walletCurrencyOptions?: NameDto<string>[]
  dateShortcutOptions?: DateShortcutOption[]
  searchFilters?: FilterQueryProps
  filterOptions: FilterOptions[]
  customLabels?: { [key in FilterOptions]?: string }
  customIcons?: { [key in FilterOptions]?: IconFCType }
  setSearchFilters: (value?: FilterQueryProps) => void
  showRebateCheckbox?: boolean
  allowFutureDates?: boolean
  size?: 'sm' | 'md' | 'lg'
  tradingAccountOptions?: NameDto<string>[]
  openExportModal?: () => void
}

export type FilterQueryOptionValue = NameDto<string | number>[] | undefined

export const TableFilter: FC<TableFilterProps> = (props) => {
  const { filterOptions, searchFilters, isVisible, size = 'md' } = props
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const filterHandlers = useFilterHandlers(props)
  if (!isVisible) return null

  return (
    <div className={styles.tableFilter}>
      <div className={styles.badgesArea}>
        <FilterHorizontalScroll scrollBehavior={filterHandlers.scrollBehavior}>
          <FilterChips
            {...props}
            searchFilters={searchFilters}
            items={filterOptions}
            onFilter={filterHandlers.handleSetSearchFilters}
          />
        </FilterHorizontalScroll>

        <div className={isMobile ? styles.mobileClearButton : styles.clearButton}>
          <Button.Secondary size={size} onClick={filterHandlers.handleClear}>
            {t('Clear Filters')}
          </Button.Secondary>
        </div>
      </div>
    </div>
  )
}

interface FilterOptionsProps extends TableFilterProps {
  items: FilterOptions[]
  onFilter(): void
}

const FilterChipsBase: React.FC<FilterOptionsProps> = (props) => {
  const { items, onFilter } = props

  return items.map((x) => (
    <ChipByOption {...props} filterOption={x} setSearchFilters={onFilter} key={String(x)} />
  ))
}

FilterChipsBase.displayName = 'FilterChips'
const FilterChips = React.memo(FilterChipsBase)
